import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { useSAL2110_R01Store } from "./Store_SAL2110_R01";
import { defaultSAL2110_R01Dto, SAL2110_R01Dto } from "./Dto_SAL2110_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'SAL2110_R01-item-total-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<SAL2110_R01Dto> | null>;
}

export const ItemTotalTable_SAL2110_R01 = (props: Props) => {
	const { gridRef } = props;

	const {
		SAL2110_R01ItemTotalList,
		itemTotalColumnState, setItemTotalColumnState,
		itemTotalScrollTop, setItemTotalScrollTop,
	} = useSAL2110_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
	} = useGridSettingContent({
		gridRef,
		key: 'rowKey',
		gridData: SAL2110_R01ItemTotalList,
		defaultGridData: defaultSAL2110_R01Dto,
		selectedRow: undefined,
	});

	const getRowId = useCallback((params: any) => {
		const data = params.data;
		if (data) {
			return data.rowKey || params.id;
		}
		return params.id;
	}, []);

	const headerNames: Record<string, string> = {
		rowIndex: "",
		itemCd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
		salesQty: '매출량',
		totalAmt: '매출액',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'itemCd')(params), []);

	const baseColumnDefs: ColDef<SAL2110_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 140,
			cellStyle: (p) => getCellStyle(p, 'itemCd'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 180,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 160,
			cellStyle: (p) => getCellStyle(p, 'itemSpec'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '매출량', colId: 'salesQty', field: 'salesQty', width: 100,
			cellStyle: (p) => getCellStyle(p, 'salesQty'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => (valueA || 0) - (valueB || 0),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '매출액', colId: 'totalAmt', field: 'totalAmt', width: 130,
			cellStyle: (p) => getCellStyle(p, 'totalAmt'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => (valueA || 0) - (valueB || 0),
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
	], [getCellStyle]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SAL2110_R01Dto>[] = useMemo(() => {
		if (itemTotalColumnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SAL2110_R01Dto>({
			baseColumnDefs,
			columnState: itemTotalColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [itemTotalColumnState, settingRowIndex, baseColumnDefs]);

	const pinnedBottomRow = useMemo(() => ([{
		itemCd: SAL2110_R01ItemTotalList.length.toLocaleString() + "건",
		salesQty: SAL2110_R01ItemTotalList.reduce((sum, item) => (sum + (item.salesQty || 0)), 0).toLocaleString(),
		totalAmt: SAL2110_R01ItemTotalList.reduce((sum, item) => (sum + (item.totalAmt || 0)), 0).toLocaleString(),
	} as any]), [SAL2110_R01ItemTotalList]);

	return (
		<GridContextWrapper<SAL2110_R01Dto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={() => {}}
			defaultRowData={defaultSAL2110_R01Dto}
			baseColumnDefs={itemTotalColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setItemTotalColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SAL2110_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<SAL2110_R01Dto>({
					idKey: 'rowKey',
					selectedList: [],
					deletedList: [],
				})}
				onRowClicked={undefined}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setItemTotalColumnState}
				scrollTop={itemTotalScrollTop}
				setScrollTop={setItemTotalScrollTop}
			/>
		</GridContextWrapper>
	);
};
